import { Component, EventEmitter, HostListener, Inject, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";

import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";

import { Observable } from "rxjs";
import { select, Store } from "@ngrx/store";
import { AppState, sitesStateIsLoading } from "src/app/store/reducers";
import { createPriceSign, updatePriceSign } from "src/app/store/actions/sites/sites.actions";

import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import { PriceSignType, SitePriceSignDTO } from "src/app/models/Sites/sitePriceSignDTO";
import { SiteGradeDTO } from "src/app/models/Sites/siteGradeDTO";
import { Dialog, GuardConfirmationDialogData } from "src/app/models/dialog/dialog.model";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { CommunicationMethodTypeCode, PROTOCOL_FORM_PARAMS } from "src/app/models/protocol/protocol.model";
import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";
import { FormUtils } from "src/app/shared/utils/formUtils";
import { setCommunicationMethodData, setProtocolFormValues } from "src/app/shared/utils/protocolFormUtils";

import { CanComponentDeactivate } from "src/app/shared";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";

const PRICE_SIGN_FORM_PARAMS = {
  enabled: "enabled",
  priceSignCode: "priceSignCode",
  serialNumber: "serialNumber",
  description: "description",
  protocolTypeCode: "protocolTypeCode",
  grades: "grades",
  gradeCode: "gradeCode",
  protocol: "protocol",
};

@Component({
  selector: "app-warehouse-form-dialog",
  templateUrl: "./price-sign-form-dialog.component.html",
  styleUrls: ["./price-sign-form-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class PriceSignFormDialogComponent implements OnInit, CanComponentDeactivate {
  @Output() getPriceSignForm: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();

  isLoading$: Observable<boolean>;

  siteCode: string;
  currentPriceSign: SitePriceSignDTO;

  priceSignTypeList: PriceSignType[];
  siteGradeList: SiteGradeDTO[];

  priceSignDataForm: FormGroup;

  isCreate: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<PriceSignFormDialogComponent>,
    private fb: FormBuilder,
    private router: Router,
    private store: Store<AppState>,
    private snackbarService: SnackbarService,
    private translate: TranslateService
  ) {
    this.isLoading$ = this.store.pipe(select(sitesStateIsLoading));
  }

  ngOnInit(): void {
    this.setDialogData();
    this.createPriceSignForm();
    this.setFormValues(this.currentPriceSign);
    if (!this.isCreate) {
      this.setGradesFormArray(this.currentPriceSign);
    }
  }

  setDialogData() {
    this.isCreate = this.data.isCreate;
    this.siteCode = this.data.siteCode;
    this.currentPriceSign = this.data.currentPriceSign;
    this.priceSignTypeList = this.data.priceSignTypeList;
    this.siteGradeList = this.data.siteGradeList;
  }

  createPriceSignForm() {
    this.priceSignDataForm = this.fb.group({
      [PRICE_SIGN_FORM_PARAMS.enabled]: [true],
      [PRICE_SIGN_FORM_PARAMS.priceSignCode]: [
        null,
        [Validators.required, Validators.min(1), Validators.max(255)],
      ],
      [PRICE_SIGN_FORM_PARAMS.serialNumber]: [null, Validators.maxLength(50)],
      [PRICE_SIGN_FORM_PARAMS.description]: [
        null,
        [Validators.required, FormUtils.noWhitespaceValidator, Validators.maxLength(45)],
      ],
      [PRICE_SIGN_FORM_PARAMS.protocolTypeCode]: [null, [Validators.required]],
      [PRICE_SIGN_FORM_PARAMS.protocol]: this.fb.group({}),
      [PRICE_SIGN_FORM_PARAMS.grades]: this.fb.array(
        [this.createGradeFormGroup()],
        [Validators.required]
      ),
    });
    this.subscribeFormChanges(this.priceSignDataForm);
  }

  subscribeFormChanges(form: FormGroup) {
    form.valueChanges.subscribe(() => {
      this.getPriceSignFormPristine();
    });
  }

  createGradeFormGroup(gradeCode?: string): FormGroup {
    return this.fb.group({
      [PRICE_SIGN_FORM_PARAMS.gradeCode]: [
        gradeCode ? gradeCode : null,
        [Validators.required],
      ],
    });
  }

  addGrade() {
    const grades = this.getFormArray(PRICE_SIGN_FORM_PARAMS.grades);
    grades.push(this.createGradeFormGroup());
  }

  removeGrade(index: number) {
    const grades = this.getFormArray(PRICE_SIGN_FORM_PARAMS.grades);
    grades.removeAt(index);
  }

  moveGradeUp(index: number) {
    const grades = this.getFormArray(PRICE_SIGN_FORM_PARAMS.grades);
    const group = grades.at(index);
    grades.removeAt(index);
    grades.insert(index - 1, group);
  }

  moveGradeDown(index: number) {
    const grades = this.getFormArray(PRICE_SIGN_FORM_PARAMS.grades);
    let group = grades.at(index);
    grades.removeAt(index);
    grades.insert(index + 1, group);
  }

  getFormArray(formParam: string): FormArray {
    return this.priceSignDataForm.get(formParam) as FormArray;
  }

  setGradesFormArray(priceSign: SitePriceSignDTO) {
    const grades = this.getFormArray(PRICE_SIGN_FORM_PARAMS.grades);
    this.clearFormArray(grades);

    priceSign.grades.forEach(({ code }) => {
      grades.push(this.createGradeFormGroup(code));
    });
  }

  clearFormArray(formArray: FormArray) {
    formArray.clear();
  }

  setFormValues(priceSign: SitePriceSignDTO, duplication?: boolean) {
    this.priceSignDataForm.controls.enabled.setValue(
      priceSign?.enabled ?? false
    );
    this.priceSignDataForm.controls.priceSignCode.setValue(
      duplication ? null : priceSign?.priceSignCode ?? null
    );
    this.priceSignDataForm.controls.serialNumber.setValue(
      duplication ? null : priceSign?.serialNumber ?? null
    );
    this.priceSignDataForm.controls.description.setValue(
      priceSign?.description ?? null
    );
    this.priceSignDataForm.controls.protocolTypeCode.setValue(
      priceSign?.protocolTypeCode ?? null
    );

    setProtocolFormValues(this.priceSignDataForm, priceSign);
  }

  duplicate() {
    this.setFormValues(this.currentPriceSign, true);

    this.isCreate = true;
    this.currentPriceSign = null;
  }

  onSubmit() {
    if (!this.priceSignDataForm.valid) {
      this.priceSignDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.formInvalid"),
        type: SnackbarType.ERROR,
      });
      return;
    }
    
    if (this.priceSignDataForm.pristine && !this.isCreate) {
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }

    if (this.invalidGrades(this.priceSignDataForm)) {
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.displicatedGrades"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    const protocolFormGroup = this.priceSignDataForm.get(
      PRICE_SIGN_FORM_PARAMS.protocol
    ) as FormGroup;
    let priceSign: SitePriceSignDTO = {
      siteCode: this.siteCode ? this.siteCode : null,
      code: this.currentPriceSign?.code,
      enabled: this.priceSignDataForm.controls.enabled.value ?? false,
      priceSignCode: this.priceSignDataForm.controls.priceSignCode.value ?? null,
      serialNumber: this.priceSignDataForm.controls.serialNumber.value ?? null,
      description: this.priceSignDataForm.controls.description.value ?? null,
      protocolTypeCode: this.priceSignDataForm.controls.protocolTypeCode.value ?? null,
      gradeCodes: this.setGradeCodes(this.priceSignDataForm),
      communicationMethodTypeCode: protocolFormGroup.get(
        PROTOCOL_FORM_PARAMS.communicationMethodTypeCode
      ).value
        ? CommunicationMethodTypeCode.SERIAL_CONNECTION
        : CommunicationMethodTypeCode.IP_PROTOCOL,
      communicationMethodData: setCommunicationMethodData(protocolFormGroup),
    };

    if (this.isCreate) {
      this.store.dispatch(createPriceSign({ priceSign }));
    } else {
      this.store.dispatch(updatePriceSign({ priceSign }));
    }

    this.priceSignDataForm.markAsUntouched();
    this.priceSignDataForm.markAsPristine();
    this.getPriceSignFormPristine();
  }

  invalidGrades(form: FormGroup): boolean {
    const grades = form.get(PRICE_SIGN_FORM_PARAMS.grades).value;

    return grades.some((gradeEl: SiteGradeDTO) => {
      const filteredGrades = grades.filter(
        (grade: SiteGradeDTO) => grade.gradeCode === gradeEl.gradeCode
      );
      return filteredGrades.length > 1;
    });
  }

  setGradeCodes(form: FormGroup): string[] {
    const grades = form.get(PRICE_SIGN_FORM_PARAMS.grades).value;

    return grades.map(({ gradeCode }) => gradeCode);
  }

  getPriceSignFormPristine() {
    this.getPriceSignForm.emit(this.priceSignDataForm);
  }

  close() {
    this.dialogRef.close();
  }
  
  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (!isAuthenticated(this.router.url) || this.priceSignDataForm.pristine) {
      return true;
    } else {
      return false;
    }
  }
  
  confirmDialog(dialog: any): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    return dialogRef.afterClosed();
  }

}
